import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { WEB3FORMS_ACCESS_KEY } from '../config';
import './ContactForm.css';

const LPA_OPTIONS = [
  { value: 'property-financial', label: 'Property and Financial Affairs' },
  { value: 'health-welfare', label: 'Health and Welfare' },
  { value: 'both', label: 'Both Types' },
];

const ContactForm = ({ isOpen = false, onClose, inline = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    lpaType: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const overlayRef = useRef(null);
  const firstInputRef = useRef(null);
  const navigate = useNavigate();

  /* Reset form when modal opens */
  useEffect(() => {
    if (isOpen && !inline) {
      setFormData({ firstName: '', lastName: '', email: '', phone: '', lpaType: '', message: '' });
      setErrors({});
      setSubmitError('');
    }
  }, [isOpen, inline]);

  /* Focus first input when modal opens */
  useEffect(() => {
    if (isOpen && !inline && firstInputRef.current) {
      const timer = setTimeout(() => {
        firstInputRef.current.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, inline]);

  /* Prevent body scroll when modal is open */
  useEffect(() => {
    if (!inline && isOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!inline) {
      document.body.style.overflow = '';
    }
    return () => {
      if (!inline) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen, inline]);

  /* Close on Escape key */
  useEffect(() => {
    if (!inline && isOpen) {
      const handleEscape = (e) => {
        if (e.key === 'Escape' && onClose) {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, inline, onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === overlayRef.current && onClose) {
        onClose();
      }
    },
    [onClose]
  );

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const handleLpaSelect = useCallback((value) => {
    setFormData((prev) => ({ ...prev, lpaType: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.lpaType;
      return next;
    });
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.lpaType) {
      newErrors.lpaType = 'Please select an LPA type';
    }

    return newErrors;
  }, [formData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSubmitError('');

      const validationErrors = validate();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setSubmitting(true);

      try {
        const lpaLabel = LPA_OPTIONS.find(o => o.value === formData.lpaType)?.label || formData.lpaType;

        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            access_key: WEB3FORMS_ACCESS_KEY,
            subject: `New LPA Enquiry from ${formData.firstName.trim()} ${formData.lastName.trim()}`,
            from_name: 'The LPA Company',
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            'LPA Type': lpaLabel,
            message: formData.message.trim() || '(No message provided)',
          }),
        });

        const data = await response.json();
        if (!data.success) {
          // eslint-disable-next-line no-console
          console.error('Web3Forms rejected the enquiry:', data.message || data);
          throw new Error('Failed to send enquiry. Please try again.');
        }

        if (!inline && onClose) {
          onClose();
        }
        const submissionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        navigate('/thank-you', { state: { fromForm: true, submissionId } });
      } catch (err) {
        setSubmitError(
          err.message || 'Something went wrong. Please try again later.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [formData, validate, navigate, inline, onClose]
  );

  /* Form content */
  const renderForm = () => (
    <form className="contact-form__form" onSubmit={handleSubmit} noValidate>
      <div className="contact-form__header">
        <h2 className="contact-form__title">Get in Touch</h2>
        <p className="contact-form__subtitle">
          Complete the form below and we will contact you to discuss your LPA requirements.
        </p>
      </div>

      <div className="contact-form__fields">
        {/* First Name + Last Name row */}
        <div className="contact-form__row">
          <div className="contact-form__field">
            <label className="contact-form__label" htmlFor="contact-firstName">
              First Name
            </label>
            <input
              ref={firstInputRef}
              id="contact-firstName"
              name="firstName"
              type="text"
              className={`contact-form__input${errors.firstName ? ' contact-form__input--error' : ''}`}
              value={formData.firstName}
              onChange={handleChange}
              placeholder="First name"
              autoComplete="given-name"
            />
            {errors.firstName && (
              <span className="contact-form__error" role="alert">
                {errors.firstName}
              </span>
            )}
          </div>

          <div className="contact-form__field">
            <label className="contact-form__label" htmlFor="contact-lastName">
              Last Name
            </label>
            <input
              id="contact-lastName"
              name="lastName"
              type="text"
              className={`contact-form__input${errors.lastName ? ' contact-form__input--error' : ''}`}
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Last name"
              autoComplete="family-name"
            />
            {errors.lastName && (
              <span className="contact-form__error" role="alert">
                {errors.lastName}
              </span>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="contact-form__field">
          <label className="contact-form__label" htmlFor="contact-email">
            Email
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            className={`contact-form__input${errors.email ? ' contact-form__input--error' : ''}`}
            value={formData.email}
            onChange={handleChange}
            placeholder="your@email.com"
            autoComplete="email"
          />
          {errors.email && (
            <span className="contact-form__error" role="alert">
              {errors.email}
            </span>
          )}
        </div>

        {/* Phone */}
        <div className="contact-form__field">
          <label className="contact-form__label" htmlFor="contact-phone">
            Phone Number
          </label>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            className={`contact-form__input${errors.phone ? ' contact-form__input--error' : ''}`}
            value={formData.phone}
            onChange={handleChange}
            placeholder="07xxx xxxxxx"
            autoComplete="tel"
          />
          {errors.phone && (
            <span className="contact-form__error" role="alert">
              {errors.phone}
            </span>
          )}
        </div>

        {/* LPA Type Selection */}
        <div className="contact-form__field">
          <label className="contact-form__label">Which type of LPA would you like to enquire about?</label>
          <div
            className={`contact-form__options${errors.lpaType ? ' contact-form__options--error' : ''}`}
            role="radiogroup"
            aria-label="Select LPA type"
          >
            {LPA_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={formData.lpaType === option.value}
                className={`contact-form__option${formData.lpaType === option.value ? ' contact-form__option--selected' : ''}`}
                onClick={() => handleLpaSelect(option.value)}
              >
                <span className="contact-form__option-radio">
                  {formData.lpaType === option.value && (
                    <span className="contact-form__option-radio-dot" />
                  )}
                </span>
                <span className="contact-form__option-label">{option.label}</span>
              </button>
            ))}
          </div>
          {errors.lpaType && (
            <span className="contact-form__error" role="alert">
              {errors.lpaType}
            </span>
          )}
        </div>

        {/* Message */}
        <div className="contact-form__field">
          <label className="contact-form__label" htmlFor="contact-message">
            Message <span className="contact-form__optional">(optional)</span>
          </label>
          <textarea
            id="contact-message"
            name="message"
            className="contact-form__textarea"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us a bit about your requirements..."
            rows="4"
          />
        </div>
      </div>

      {/* Submit error */}
      {submitError && (
        <div className="contact-form__submit-error" role="alert">
          {submitError}
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        className="contact-form__submit"
        disabled={submitting}
      >
        {submitting ? (
          <>
            <span className="contact-form__spinner" aria-hidden="true" />
            Sending...
          </>
        ) : (
          'Send Enquiry'
        )}
      </button>
    </form>
  );

  /* Inline mode */
  if (inline) {
    return (
      <div className="contact-form contact-form--inline">
        {renderForm()}
      </div>
    );
  }

  /* Modal mode */
  if (!isOpen) return null;

  return (
    <div
      className="contact-form__overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Contact form"
    >
      <div className="contact-form contact-form--modal">
        <button
          type="button"
          className="contact-form__close"
          onClick={onClose}
          aria-label="Close contact form"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M5 5L15 15M15 5L5 15"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
        {renderForm()}
      </div>
    </div>
  );
};

export default ContactForm;
